import * as bootstrap from 'bootstrap';
import { notifier } from './util';

// ────────────────────────────────────────────────────────────
// Interfaces
// ────────────────────────────────────────────────────────────

interface CSVRow {
    index: number;
    data: string[];
    is_valid: boolean;
    error: string | null;
    include: boolean;
}

export interface ImportResult {
    success: boolean;
    imported_count: number;
    skipped_count: number;
    queued_review: number;
    already_imported: boolean;
    message?: string;
    // Legacy field aliases returned by the handler
    imported?: number;
    skipped?: number;
    // Phase 152 amazon_order fields — present only when bank === 'amazon_order'
    bank?: string;
    staged?: number;
    auto_split?: number;
    needs_matching?: number;
    batch_id?: number;
    matching_url?: string;
}

export interface ImportModalOptions {
    onSuccess: (result: ImportResult) => void;
}

// ────────────────────────────────────────────────────────────
// Module-level state (one instance per page — modal is singleton)
// ────────────────────────────────────────────────────────────

let parsedRows: CSVRow[] = [];
let selectedFile: File | null = null;

// Header-column index map built from the first CSV row.
// Keys are normalised lowercase header names; values are column indices.
let headerIndex: Record<string, number> = {};

// True when the uploaded file is an Amazon order-history export (browser or official). Banks
// name the date column 'Date'/'Transaction Date'; only Amazon uses 'Order Date'. Amazon line
// items can legitimately be $0 (free / fully-discounted / cancelled items), so the preview must
// not reject them on amount — the server (fgetcsv + field-map + reconciliation) is authoritative.
let isAmazonFormat = false;

// True when the uploaded file is a PayPal Activity export. PayPal dates are DD/MM/YYYY
// (day-first); JS Date.parse() reads "/"-dates as US MM/DD and returns NaN for any day>12
// (e.g. 18/07/2019), which wrongly flags ~half the rows "Invalid date" in the preview. The
// server (PaypalImportModel) re-parses with createFromFormat('d/m/Y H:i:s'), so — like Amazon —
// the preview must defer date validation to the server. Signature mirrors BankCsvFormat::PAYPAL_SIGNATURE.
let isPaypalFormat = false;

// index.ts runs EVERY page module's init() on EVERY page, so both the Transactions and
// Home hosts call initImportModal() against this one shared modal. Bind the DOM listeners
// exactly once (a second binding would fire the import POST twice) and collect each host's
// onSuccess so all registered callbacks run after a successful import. Each host's callback
// self-guards its own side effect (datatable reload / chart redraw).
let modalInitialised = false;
const successCallbacks: Array<(result: ImportResult) => void> = [];

// ────────────────────────────────────────────────────────────
// CSV parsing helpers (extracted verbatim from transactions.ts)
// ────────────────────────────────────────────────────────────

function buildHeaderIndex(headerRow: string[]): void {
    headerIndex = {};
    headerRow.forEach((col, i) => {
        headerIndex[col.toLowerCase().trim()] = i;
    });
    isAmazonFormat = headerIndex['order date'] !== undefined;
    isPaypalFormat = headerIndex['transaction id'] !== undefined
        && headerIndex['exchange rate'] !== undefined
        && headerIndex['item title'] !== undefined;
}

// Locate a column index by trying a list of known header names (RBC + Simplii variants).
// Returns -1 if none found.
function colIdx(candidates: string[]): number {
    for (const c of candidates) {
        if (headerIndex[c] !== undefined) return headerIndex[c];
    }
    return -1;
}

// Quote-aware CSV field split for one line. A naive line.split(',') mis-aligns every row
// whose quoted fields contain commas — the official Amazon "Order History.csv" has commas
// inside Product Name / addresses on ~67% of rows, which shifted the date/amount columns and
// failed the preview (Phase 152 UAT). Handles "" escaped quotes; surrounding quotes stripped.
// (No embedded-newline handling: this file's line count == row count, and the preview already
// splits on newlines upstream. The server uses fgetcsv for the authoritative parse.)
function splitCsvLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') { cur += '"'; i++; } // escaped "" → literal "
                else inQuotes = false;
            } else {
                cur += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            out.push(cur);
            cur = '';
        } else {
            cur += ch;
        }
    }
    out.push(cur);
    return out;
}

function validateRow(data: string[]): { is_valid: boolean; error: string | null } {
    // Header-aware: use detected column positions when available.
    // Falls back to heuristic positional read for files where buildHeaderIndex was not called.
    // Bank exports name the date "Date"/"Transaction Date"; Amazon exports name it "Order Date"
    // and the amount "Total Amount"/"Unit Price" (official "Order History.csv" puts ASIN in
    // column 0). Without the Amazon names the date fell back to data[0] (the ASIN) and rejected
    // every Amazon row ("Invalid date: <ASIN>", Phase 152 UAT). The server re-parses the raw
    // file with the real field-map, so this preview only needs to recognise the row as valid.
    const dateIdx   = colIdx(['date', 'transaction date', 'order date']);
    const amountIdx = colIdx(['cad$', 'debit amount', 'credit amount', 'amount', 'funds out', 'funds in', 'total amount', 'unit price', 'item price']);

    const dateVal = dateIdx >= 0 ? (data[dateIdx] ?? '') : (data[0] ?? '');

    // PayPal dates are DD/MM/YYYY (day-first). JS Date.parse() reads "/"-dates as US MM/DD and
    // returns NaN for every day>12 row (e.g. 18/07/2019), wrongly rejecting valid rows. The server
    // re-parses with createFromFormat('d/m/Y H:i:s'), so the preview only needs the date present.
    // (Same server-owns-validation rationale as the Amazon branch below.)
    if (isPaypalFormat) {
        return dateVal
            ? { is_valid: true, error: null }
            : { is_valid: false, error: 'Invalid date: (empty)' };
    }

    if (!dateVal || isNaN(Date.parse(dateVal))) {
        return { is_valid: false, error: `Invalid date: ${dateVal}` };
    }

    // Amazon line items can legitimately be $0 (free / fully-discounted / cancelled). The server
    // re-parses with fgetcsv + the real field-map and owns amount/dedup/reconciliation, so the
    // preview only needs a parseable Order Date here. Bank CSVs still require a non-zero amount.
    if (isAmazonFormat) {
        return { is_valid: true, error: null };
    }

    // For the amount: if we have separate debit/credit columns, treat both as valid.
    // Real Simplii exports use 'Funds Out'/'Funds In' (WR-02), not 'debit/credit amount'.
    // If we have a unified amount column (RBC CAD$), any non-zero value is valid (credits included).
    const debitIdx  = colIdx(['debit amount', 'funds out']);
    const creditIdx = colIdx(['credit amount', 'funds in']);
    let amountVal: number;
    if (debitIdx >= 0 || creditIdx >= 0) {
        const debit  = parseFloat(data[debitIdx]  ?? '') || 0;
        const credit = parseFloat(data[creditIdx] ?? '') || 0;
        amountVal = Math.abs(debit) + Math.abs(credit);
    } else if (amountIdx >= 0) {
        amountVal = Math.abs(parseFloat(data[amountIdx] ?? '') || 0);
    } else {
        // Fallback: try common positional reads (column 2 for 5-col, column 6 for 8-col)
        const len = data.length;
        const raw = len >= 8 ? data[6] : data[2];
        amountVal = Math.abs(parseFloat(raw ?? '') || 0);
    }

    if (amountVal === 0) {
        return { is_valid: false, error: 'Amount is zero or invalid' };
    }

    return { is_valid: true, error: null };
}

function updateSummary(): void {
    const summary = document.querySelector('.js-import-preview-summary') as HTMLElement;
    if (!summary) return;
    const selected = parsedRows.filter(r => r.include).length;
    const total    = parsedRows.length;
    if (selected === 0) {
        summary.textContent = 'No rows selected — check at least one to import';
    } else {
        summary.textContent = `${selected} of ${total} rows selected`;
    }
}

function renderPreviewTable(rows: CSVRow[]): void {
    const zone    = document.querySelector('.js-import-drop-zone') as HTMLElement;
    const preview = document.querySelector('.js-import-csv-preview') as HTMLElement;
    const table   = document.querySelector('.js-import-preview-table') as HTMLTableElement;
    if (!zone || !preview || !table) return;

    zone.classList.add('d-none');
    preview.classList.remove('d-none');

    const thead = table.querySelector('thead') as HTMLTableSectionElement;
    const tbody = table.querySelector('tbody') as HTMLTableSectionElement;
    thead.innerHTML = '';
    tbody.innerHTML = '';

    // Build header row with select-all checkbox
    const headerRow = document.createElement('tr');
    const selectAllTh = document.createElement('th');
    const selectAllCb = document.createElement('input');
    selectAllCb.type = 'checkbox';
    selectAllCb.checked = rows.some(r => r.is_valid && r.include);
    selectAllCb.setAttribute('aria-label', 'Select all rows');
    selectAllCb.addEventListener('change', () => {
        parsedRows.forEach(r => {
            if (r.is_valid) r.include = selectAllCb.checked;
        });
        renderPreviewTable(parsedRows);
    });
    selectAllTh.appendChild(selectAllCb);
    headerRow.appendChild(selectAllTh);
    ['#', 'Date', 'Name', 'Amount', 'Status'].forEach(text => {
        const th = document.createElement('th');
        th.textContent = text;
        headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);

    // Build body rows
    rows.forEach((row, i) => {
        const tr = document.createElement('tr');
        if (!row.is_valid) tr.classList.add('table-danger');

        // Checkbox cell
        const cbTd = document.createElement('td');
        cbTd.style.minHeight = '44px';
        cbTd.style.minWidth  = '44px';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = row.include;
        if (!row.is_valid) {
            cb.disabled = true;
            cb.checked  = false;
        }
        cb.addEventListener('change', () => {
            parsedRows[i].include = cb.checked;
            updateSummary();
        });
        cbTd.appendChild(cb);
        tr.appendChild(cbTd);

        // Index cell
        const indexTd = document.createElement('td');
        indexTd.textContent = String(row.index + 1);
        tr.appendChild(indexTd);

        // Date, Name, Amount cells — header-aware (falls back to positional).
        // Amazon exports name these Order Date / Product Name (or Item Title) / Total Amount,
        // so without them the preview displayed the ASIN, address, and a Gift field (UAT).
        const dIdx  = colIdx(['date', 'transaction date', 'order date']);
        const nIdx  = colIdx(['description 1', 'description 2', 'name', 'description', 'product name', 'item title']);
        const debitI  = colIdx(['debit amount', 'funds out']);
        const creditI = colIdx(['credit amount', 'funds in']);
        const aIdx  = colIdx(['cad$', 'amount', 'total amount', 'unit price', 'item price']);

        const date = dIdx >= 0 ? (row.data[dIdx]  ?? '') : (row.data[0] ?? '');
        const name = nIdx >= 0 ? (row.data[nIdx]  ?? '') : (row.data[1] ?? '');
        let amount: string;
        if (debitI >= 0 || creditI >= 0) {
            const d = (debitI  >= 0 ? row.data[debitI]  : '') ?? '';
            const c = (creditI >= 0 ? row.data[creditI] : '') ?? '';
            amount = (d && d !== '0' && d !== '') ? d : c;
        } else if (aIdx >= 0) {
            amount = row.data[aIdx] ?? '';
        } else {
            amount = row.data.length >= 8 ? (row.data[6] ?? '') : (row.data[2] ?? '');
        }

        [date, name, amount].forEach(val => {
            const td = document.createElement('td');
            td.textContent = val ?? '';
            tr.appendChild(td);
        });

        // Status cell — invalid rows get a .badge.bg-danger
        const statusTd = document.createElement('td');
        if (!row.is_valid) {
            const badge = document.createElement('span');
            badge.className = 'badge bg-danger ms-2';
            badge.textContent = `Invalid row: ${row.error ?? 'Invalid'}`;
            statusTd.appendChild(badge);
        } else {
            statusTd.innerHTML = '<span class="text-success">OK</span>';
        }
        tr.appendChild(statusTd);

        tbody.appendChild(tr);
    });

    updateSummary();
}

function parseCSVPreview(file: File): void {
    selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
        let text = (e.target?.result as string) ?? '';
        // Strip BOM
        text = text.replace(/^﻿/, '');

        const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
        // Parse header row to build the column-index map for header-aware validation.
        const headerCols = lines[0]
            ? splitCsvLine(lines[0]).map(cell => cell.trim())
            : [];
        buildHeaderIndex(headerCols);
        // Skip header row (first line)
        const dataLines = lines.slice(1);

        // Cap at 500 rows
        const capped = dataLines.length > 500;
        const rowsToProcess = capped ? dataLines.slice(0, 500) : dataLines;

        parsedRows = rowsToProcess.map((line, idx) => {
            const data = splitCsvLine(line).map(cell => cell.trim());
            const { is_valid, error } = validateRow(data);
            return {
                index: idx,
                data,
                is_valid,
                error,
                include: is_valid,
            };
        });

        // Show warning banner if capped
        const preview = document.querySelector('.js-import-csv-preview') as HTMLElement;
        const existingWarn = preview?.querySelector('.js-cap-warning');
        if (existingWarn) existingWarn.remove();
        if (capped && preview) {
            const warn = document.createElement('div');
            warn.className = 'alert alert-warning js-cap-warning';
            warn.textContent = `Showing first 500 rows. Import will include all ${dataLines.length} rows.`;
            preview.insertBefore(warn, preview.firstChild);
        }

        renderPreviewTable(parsedRows);
    };
    reader.readAsText(file);
}

// ────────────────────────────────────────────────────────────
// Toast helper
// ────────────────────────────────────────────────────────────

function showToast(result: ImportResult, reviewUrl: string): void {
    // ── amazon_order response variant ─────────────────────────────────────
    // Added BEFORE the bank-CSV logic (Phase 152 D-07).
    // Each case returns early to prevent fallthrough into bank-CSV copy.
    if (result.bank === 'amazon_order') {
        const staged    = result.staged ?? 0;
        const autoSplit = result.auto_split ?? 0;
        const needsM    = result.needs_matching ?? 0;
        const matchUrl  = result.matching_url ?? '/transactions/amazon';

        if (staged === 0) {
            // Case 3: already imported (byte-identical re-upload or 0 new items)
            notifier.info('Already imported — this file has been uploaded before (0 new items).');
            return;
        }
        if (needsM > 0) {
            // Case 1: staged items with some needing matching — sticky toast with deep-link
            notifier.success(
                `Amazon order import complete.<br>` +
                `<span class="small">${staged} items staged &middot; ${autoSplit} auto-split</span><br>` +
                `<a href="${matchUrl}" style="color:inherit">${needsM} order${needsM !== 1 ? 's' : ''} need matching &rarr;</a>`,
                { durations: { success: 0 } }  // sticky — deep-link must remain clickable
            );
        } else {
            // Case 2: all staged items auto-matched — short success toast
            notifier.success(`Amazon import complete — ${staged} items staged, all auto-matched!`);
        }
        return;
    }
    // ── PayPal response variant (WR-01) ──────────────────────────────────
    // PayPal sets `staged` (not imported_count/imported); without this branch the generic
    // bank-CSV copy below always rendered "0 imported" for successful PayPal imports.
    if (result.bank === 'paypal') {
        const staged  = result.staged  ?? 0;
        const skipped = result.skipped ?? 0;
        notifier.success(`PayPal import — ${staged} imported, ${skipped} skipped as duplicates.`);
        return;
    }
    // ── Bank-CSV response (RBC / Simplii) — unchanged ────────────────────
    const imported = result.imported_count ?? result.imported ?? 0;
    const skipped  = result.skipped_count  ?? result.skipped  ?? 0;
    const queued   = result.queued_review  ?? 0;

    if (result.already_imported) {
        notifier.info('Already imported — 0 new rows.');
    } else if (queued > 0) {
        // Sticky toast (duration 0 = no auto-dismiss per AWN toast.js:39)
        notifier.success(
            `${imported} imported, ${skipped} skipped as duplicates — ` +
            `<a href="${reviewUrl}" style="color:inherit">${queued} queued for review →</a>`,
            { durations: { success: 0 } }
        );
    } else {
        // Short toast (inherits global 1000ms from util.ts:114)
        notifier.success(`${imported} imported, ${skipped} skipped as duplicates.`);
    }
}

// ────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────

export function initImportModal(options: ImportModalOptions): void {
    const modalEl = document.getElementById('import-csv-modal');
    if (!modalEl) return;

    const zone       = modalEl.querySelector('.js-import-drop-zone') as HTMLElement;
    const fileInput  = modalEl.querySelector('.js-import-csv-input') as HTMLInputElement;
    const confirmBtn = modalEl.querySelector('.js-import-confirm') as HTMLButtonElement;
    const preview    = modalEl.querySelector('.js-import-csv-preview') as HTMLElement;
    const spinner    = modalEl.querySelector('.js-import-spinner') as HTMLElement;
    const resultEl   = modalEl.querySelector('.js-import-result') as HTMLElement;

    if (!zone || !fileInput || !confirmBtn) return;

    // Register this host's success callback, then bind DOM listeners only once (see the
    // modalInitialised note above) — both hosts share this single modal element.
    successCallbacks.push(options.onSuccess);
    if (modalInitialised) return;
    modalInitialised = true;

    const bsModal = new bootstrap.Modal(modalEl, { backdrop: true, keyboard: true });

    // Reset to the initial drop-zone state whenever the modal closes, so the NEXT open is
    // clean. Without this, after one import the drop zone stays hidden (renderPreviewTable hid
    // it) and preview/spinner are also hidden → the modal opens EMPTY and the operator must
    // refresh the page to import another file (surfaced importing 8 PayPal files sequentially).
    function resetModalState(): void {
        parsedRows = [];
        selectedFile = null;
        isPaypalFormat = false;
        isAmazonFormat = false;
        if (fileInput) fileInput.value = '';
        if (zone) zone.classList.remove('d-none');
        if (preview) {
            preview.classList.add('d-none');
            const warn = preview.querySelector('.js-cap-warning');
            if (warn) warn.remove();
        }
        if (spinner) spinner.classList.add('d-none');
        if (resultEl) { resultEl.innerHTML = ''; resultEl.classList.add('d-none'); }
        const tbody = modalEl.querySelector('.js-import-preview-table tbody');
        if (tbody) tbody.innerHTML = '';
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Import Checked Rows';
    }

    modalEl.addEventListener('hidden.bs.modal', resetModalState);

    // The import handler is a signal on TransactionsPresenter. This modal is shared across
    // presenters (Home + Transactions), so a relative ?do= would dispatch to the WRONG
    // presenter when the modal is launched from Home (→ 403 BadSignalException). Pin it to
    // Transactions via data-import-url; fall back to the relative signal if the attr is absent.
    const importBase = modalEl.getAttribute('data-import-url') || '';
    const importUrl = importBase
        ? importBase + (importBase.includes('?') ? '&' : '?') + 'do=importTransactions'
        : '?do=importTransactions';

    // ── Drop-zone events ──────────────────────────────────────

    zone.addEventListener('dragover', (e) => {
        e.preventDefault();
        zone.classList.add('border-primary');
    });

    zone.addEventListener('dragleave', () => {
        zone.classList.remove('border-primary');
    });

    zone.addEventListener('drop', (e) => {
        e.preventDefault();
        zone.classList.remove('border-primary');
        const file = (e as DragEvent).dataTransfer?.files[0];
        if (file) parseCSVPreview(file);
    });

    zone.addEventListener('click', (e) => {
        // Don't re-trigger if click originated from the file input itself
        if ((e.target as HTMLElement) === fileInput) return;
        fileInput.click();
    });

    fileInput.addEventListener('change', () => {
        if (fileInput.files?.[0]) {
            parseCSVPreview(fileInput.files[0]);
        }
    });

    // ── Confirm-import handler ────────────────────────────────

    confirmBtn.addEventListener('click', () => {
        if (!selectedFile) return;

        const excludedIndices = parsedRows.filter(r => !r.include).map(r => r.index);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('excluded_rows', JSON.stringify(excludedIndices));

        // Show loading state
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Importing…';
        const dialog = modalEl.querySelector('.modal-dialog') as HTMLElement;
        if (dialog) dialog.setAttribute('aria-busy', 'true');
        if (preview) preview.classList.add('d-none');
        if (spinner) spinner.classList.remove('d-none');

        fetch(importUrl, {
            method: 'POST',
            body: formData,
        })
        .then(response => response.json())
        .then((data: ImportResult) => {
            // Hide spinner
            if (spinner) spinner.classList.add('d-none');
            if (dialog) dialog.removeAttribute('aria-busy');

            if (data.success && !data.already_imported) {
                // SUCCESS: auto-close modal, fire toast, invoke host callback (D-06/D-07)
                const reviewUrl = modalEl.getAttribute('data-review-url') ?? '/transactions/review';
                bsModal.hide();
                showToast(data, reviewUrl);
                // Fire EVERY registered host callback; each self-guards its own side effect
                // (Transactions → datatable.ajax.reload(); Home → in-place chart redraw).
                successCallbacks.forEach(cb => cb(data));
            } else if (data.already_imported) {
                // Already imported — show info toast, keep modal open for operator awareness
                notifier.info('Already imported — 0 new rows.');
                if (preview) preview.classList.remove('d-none');
                confirmBtn.disabled = false;
                confirmBtn.textContent = 'Import Checked Rows';
            } else {
                // TOP-LEVEL FAILURE: show inline error, modal stays open, no reload
                if (preview) preview.classList.remove('d-none');
                if (resultEl) {
                    const msg = data.message || 'server error';
                    resultEl.innerHTML =
                        `<div class="alert alert-danger">Could not import: ${msg}. Check the file format and try again.</div>`;
                    resultEl.classList.remove('d-none');
                }
                confirmBtn.disabled = false;
                confirmBtn.textContent = 'Import Checked Rows';
            }
        })
        .catch(() => {
            // NETWORK FAILURE: show fallback inline error, modal stays open
            if (spinner) spinner.classList.add('d-none');
            if (dialog) dialog.removeAttribute('aria-busy');
            if (preview) preview.classList.remove('d-none');
            if (resultEl) {
                resultEl.innerHTML =
                    '<div class="alert alert-danger">Could not import: server error. Check your connection and try again.</div>';
                resultEl.classList.remove('d-none');
            }
            confirmBtn.disabled = false;
            confirmBtn.textContent = 'Import Checked Rows';
        });
    });
}
